import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../helpers/constants";
import {
  ProjectInvoiceSummary,
  ProjectInvoicingLanguage,
  ViewProjectInvoiceResponse,
} from "../../../pages/createProject/modelsAndSchemas/Models";
import {
  ActivityTypeList,
  ProjectDetails,
  ProjectList,
} from "../../../pages/viewProject/modelsAndSchemas/models";
import apiInstance from "../../instances/apiInstance";
import {
  GetProgramFeeResponse,
  IdNameList,
  ReverseInvoiceParams,
  SaveProgramFeeParams,
} from "../common/models";
import {
  AddExistingProjectDeliverableParams,
  ChildProgramsListResponse,
  CreateProjectKeyDetailsPayload,
  CreateUpdateChildProgramsPayload,
  GetProjectActivationResponse,
  GetProjectInvoicingLanguagesParams,
  GetProjectListPayload,
  GetProjectListResponse,
  ProjectActivityPayload,
  ProjectAddFeeOrExpenseRequestPayload,
  ProjectDeliverableParams,
  ProjectExpenseData,
  ProjectKeyDetailsResponse,
  ProjectParticipantActivityParams,
  ProjectPurchaseOrderExpensePayload,
  ProjectStatusParams,
  SaveOrEditProjectPrimaryDetailsPayload,
  SaveProjectStatusParams,
  Topic,
  TopicDetails,
  UpdateProjectKeyDetailsPayload,
  ViewProjectFeeOrExpenseDetails,
} from "./model";

export const useSearchProjectListMutation = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const res = await apiInstance.post(API_ENDPOINTS.SEARCH_PROJECT, params);
      return res.data as ProjectList;
    },
  });
};

export const useSearchProjectSavedFiltersQuery = ({
  searchPageName,
}: {
  searchPageName: any;
}) => {
  return useQuery({
    queryKey: ["getPageName", `${searchPageName}`],
    queryFn: async () => {
      return [] as any;
    },
  });
};

export const useGetProjectKeyDetailsById = () => {
  const [keyDetailsId, setKeyDetailsId] = useState<string>();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["useGetProjectKeyDetailsById", keyDetailsId],
    queryFn: async () => {
      return (
        await apiInstance.get<ProjectKeyDetailsResponse>(
          API_ENDPOINTS.GET_PROJECT_KEY_DETAILS(keyDetailsId),
        )
      ).data;
    },
    enabled: Boolean(keyDetailsId),
    retry: false,
  });

  const refetchWithParams = (projectId: string) => {
    if (keyDetailsId === projectId) {
      queryClient.invalidateQueries({
        queryKey: ["useGetProjectKeyDetailsById", projectId],
      });
    }
    setKeyDetailsId(projectId);
  };

  return { ...query, refetchWithParams };
};

export const useCreateProjectKeyDetailsMutation = () => {
  return useMutation({
    mutationFn: async (params: CreateProjectKeyDetailsPayload) => {
      const response = await apiInstance.post<{ id: string }>(
        API_ENDPOINTS.CREATE_PROJECT_KEY_DETAILS,
        params,
      );
      return response.data as { id: string };
    },
  });
};

export const useUpdateProjectKeyDetailsMutation = () => {
  return useMutation({
    mutationFn: async (params: UpdateProjectKeyDetailsPayload) => {
      const response = await apiInstance.put(
        API_ENDPOINTS.UPDATE_PROJECT_KEY_DETAILS(params.projectId),
        params,
      );
      return response.data as ProjectKeyDetailsResponse;
    },
  });
};

export const useCreateUpdateChildProgramsMutation = () => {
  return useMutation({
    mutationFn: async (params: CreateUpdateChildProgramsPayload) => {
      const response = await apiInstance.put(
        API_ENDPOINTS.createUpdateChildPrograms(params.projectId),
        params,
      );
      return response.data;
    },
  });
};

export const useSaveProjectPrimaryDetailsMutation = () => {
  return useMutation({
    mutationFn: async (params: SaveOrEditProjectPrimaryDetailsPayload) => {
      const { id, ...restParams } = params;
      const response = await apiInstance.post<{ id: string }>(
        API_ENDPOINTS.saveProjectPrimaryDetailById(id),
        restParams,
      );
      return response.data;
    },
  });
};

export const useUpdateProjectPrimaryDetailsMutation = () => {
  return useMutation({
    mutationFn: async (params: SaveOrEditProjectPrimaryDetailsPayload) => {
      const { id, ...restParams } = params;
      const response = await apiInstance.put<{ id: string }>(
        API_ENDPOINTS.updateProjectPrimaryDetailById(id),
        restParams,
      );
      return response.data;
    },
  });
};

export const useGetProjectFeeDetails = () => {
  const [projectId, setProjectId] = useState<string>();

  const query = useQuery({
    queryKey: ["useGetProjectFeeDetails", projectId],
    queryFn: async () => {
      const response = await apiInstance.get<GetProgramFeeResponse>(
        API_ENDPOINTS.getProjectFeeDetails(projectId as string),
      );
      return response.data as GetProgramFeeResponse;
    },
    enabled: Boolean(projectId),
    retry: false,
  });

  const refetchWithParams = (projectId: string) => {
    setProjectId(projectId);
  };

  return { ...query, refetchWithParams };
};

export const useSaveProjectFeeDetailsMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      projectId,
    }: {
      params: SaveProgramFeeParams;
      projectId: string;
    }) => {
      const response = apiInstance.post(
        API_ENDPOINTS.saveProjectFeeDetails(projectId),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useGetProjectPrimaryDetailsById = () => {
  const [projectId, setProjectId] = useState<string>();

  const query = useQuery({
    queryKey: ["useGetProjectPrimaryDetailsById", projectId],
    queryFn: async () => {
      const response = await apiInstance.get(
        API_ENDPOINTS.GET_PROJECT_PRIMARY_DETAILS(projectId ?? ""),
      );
      return response.data as ProjectDetails;
    },
    enabled: Boolean(projectId),
    retry: false,
  });

  const refetchWithParams = (id: string) => {
    setProjectId(id);
  };

  return { ...query, refetchWithParams };
};

export const useGetProjectFeesAndExpenses = () => {
  return useMutation({
    mutationFn: async (projectId: string): Promise<ProjectExpenseData[]> => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getProjectFeesAndExpenseList(projectId ?? ""),
      );
      return res.data;
    },
    retry: false,
  });
};

export const useCreateProjectExpenseMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      projectId,
    }: {
      params: ProjectAddFeeOrExpenseRequestPayload;
      projectId: string;
    }) => {
      const response = apiInstance.post(
        API_ENDPOINTS.createOrEditProjectFeeOrExpense(projectId ?? ""),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useEditProjectExpenseMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      projectId,
    }: {
      params: ProjectAddFeeOrExpenseRequestPayload;
      projectId: string;
    }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.createOrEditProjectFeeOrExpense(projectId ?? ""),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useEditProjectExpenseDetails = () => {
  return useMutation({
    mutationFn: async ({
      params,
      expenseId,
    }: {
      params: ProjectAddFeeOrExpenseRequestPayload;
      expenseId: string;
    }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.getProjectExpenseById(expenseId),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useGetProjectFeesOrExpenseById = () => {
  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getProjectExpenseById(id),
      );
      return res.data as ViewProjectFeeOrExpenseDetails;
    },
    retry: false,
  });

  return {
    ...mutation,
    fetchById: mutation.mutateAsync, // you call this with an ID
  };
};

export const useGetProjectInvoicingLanguages = () => {
  const [params, setParams] = useState<GetProjectInvoicingLanguagesParams>();
  const query = useQuery<ProjectInvoicingLanguage[]>({
    queryKey: ["getProjectInvoicingLanguages", params],
    queryFn: async (): Promise<ProjectInvoicingLanguage[]> => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getProjectInvoicingLanguages(
          params?.clientId,
          params?.projectId,
        ),
      );
      return res.data;
    },
    retry: false,
  });

  const refetchWithParams = (newParams: GetProjectInvoicingLanguagesParams) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

export const useGetProjectInvoiceByIdMutation = () => {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getProjectInvoiceById(invoiceId),
      );
      return res.data as ViewProjectInvoiceResponse;
    },
  });
};

export const useGetInvoiceHistoryByProjectId = () => {
  const [params, setParams] = useState<string>();

  const query = useQuery({
    queryKey: ["GetProjectInvoiceList", params],
    queryFn: async () => {
      if (!params) return null;
      const res = await apiInstance.get(
        API_ENDPOINTS.getInvoicesByProjectId(params ?? ""),
      );
      return res.data as ProjectInvoiceSummary;
    },
    enabled: !!params,
  });

  const refetchWithParams = (programId: string) => {
    setParams(programId);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useProjectReverseInvoiceMutation = () => {
  return useMutation({
    mutationFn: async ({
      invoiceId,
      date,
      reason,
      comments,
    }: ReverseInvoiceParams) => {
      const res = await apiInstance.post(
        API_ENDPOINTS.getProjectInvoiceById(invoiceId),
        {
          action: "reverse",
          date,
          reason,
          comments,
        },
      );
      return res.data;
    },
  });
};

export const useCreateProjectInvoiceMutation = () => {
  return useMutation<
    { id: string; number: string },
    Error,
    { projectId: string; payload: ProjectPurchaseOrderExpensePayload }
  >({
    mutationFn: ({
      projectId,
      payload,
    }: {
      projectId: string;
      payload: ProjectPurchaseOrderExpensePayload;
    }) => {
      return apiInstance
        .post(API_ENDPOINTS.createInvoiceByProjectId(projectId), payload)
        .then((res) => res.data as { id: string; number: string });
    },
  });
};

// this Api for extrafield required in projectDetails Api
export const useGetViewProjectDetailById = () => {
  const [params, setParams] = useState<string>();

  const query = useQuery({
    queryKey: ["getViewProjectDetailById", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getViewProjectDetailById(params),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useUpdateProjectFeeDetailsMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      projectId,
    }: {
      params: SaveProgramFeeParams;
      projectId: string;
    }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.updateProjectFeeDetailsByProjectId(projectId ?? ""),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useGetProjectList = () => {
  const [params, setParams] = useState<GetProjectListPayload>();

  const query = useQuery({
    queryKey: ["getProjectList", params],
    queryFn: async (): Promise<GetProjectListResponse[]> => {
      if (!params) {
        return [];
      }
      const { keyword = "", clientId } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_PROJECT_LIST_AUTOCOMPLETE(keyword, clientId),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: GetProjectListPayload) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useDeleteProjectDeliverableMutation = () => {
  return useMutation<null, Error, any>({
    mutationFn: ({ deliverableId }: { deliverableId: string }) => {
      return apiInstance.delete(
        API_ENDPOINTS.getProjectDeliverableById(deliverableId),
      );
    },
  });
};

export const useGetProjectActivityById = (topicId: string) => {
  return useQuery({
    queryKey: ["getProjectActivityById", topicId],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getProjectTopicById(topicId),
      );
      return res.data as TopicDetails;
    },
    retry: false,
  });
};

export const useGetProjectActivitiesByProjectId = () => {
  const [params, setParams] = useState<{
    projectId: string;
    isActive?: boolean;
  }>();  
  const query = useQuery({
    queryKey: ["getProjectActivities", params],
    queryFn: async () => {
      if (!params?.projectId) {
        return [];
      }
      const queryParams = new URLSearchParams();
      if (params?.isActive) {
        queryParams.append("isActive", "true");
      }
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getTopicListByProjectId(params.projectId)}?${queryParams.toString()}`,
      );
      return res.data as Topic[];
    },
    enabled: !!params?.projectId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (projectId: string, isActive?: boolean) => {
    setParams({ projectId, isActive });
  };

  const clearListOfProjectTopic = () => {
    setParams(undefined);
    // it will return [] array
  };
  return { ...query, refetchWithParams, clearListOfProjectTopic };
};
export const useDeleteProjectActivityMutation = () => {
  return useMutation<null, Error, any>({
    mutationFn: ({ topicId }: { topicId: string }) => {
      return apiInstance.delete(
        API_ENDPOINTS.getProjectTopicById(topicId ?? ""),
      );
    },
  });
};

export const useGetProjectDeliverablesByProjectId = () => {
  const [projectId, setProjectId] = useState<string>();
  const query = useQuery({
    queryKey: ["getProjectDeliverables", projectId],
    queryFn: async () => {
      if (!projectId) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getProjectDeliverables(projectId),
      );
      return res.data;
    },
    enabled: !!projectId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (projectId: string) => {
    setProjectId(projectId);
  };
  return { ...query, refetchWithParams };
};

export const useProjectActivityMutation = () => {
  return useMutation({
    mutationFn: ({
      payload,
      projectId,
    }: {
      payload: ProjectActivityPayload;
      projectId: string;
    }) =>
      apiInstance.post(
        API_ENDPOINTS.getTopicListByProjectId(projectId),
        payload,
      ),
  });
};

export const useProjectActivityUpdateMutation = () => {
  return useMutation({
    mutationFn: ({
      payload: { id, ...rest },
    }: {
      payload: ProjectActivityPayload;
    }) => {
      return apiInstance.put(API_ENDPOINTS.getProjectTopicById(id!), rest);
    },
  });
};

export const useGetExistingProjectDeliverables = () => {
  const [isCall, setIsCall] = useState<boolean>(false);
  const query = useQuery({
    queryKey: ["getExistingProjectDeliverables"],
    queryFn: async () => {
      if (!isCall) {
        return [];
      }
      const res = await apiInstance.get(
        `${API_ENDPOINTS.MDM_BASE_URL}/deliverables/master`,
      );
      return res.data;
    },
    retry: false,
    enabled: !!isCall,
  });
  const refetchWithParams = () => {
    setIsCall(true);
  };
  return { ...query, refetchWithParams };
};

export const useAddNewProjectDeliverableMutation = () => {
  return useMutation({
    mutationFn: ({
      params,
      projectId,
    }: {
      params: ProjectDeliverableParams;
      projectId: string;
    }) => {
      return apiInstance.post(
        API_ENDPOINTS.getProjectDeliverables(projectId),
        params,
      );
    },
  });
};

export const useUpdateProjectDeliverableMutation = () => {
  return useMutation({
    mutationFn: ({
      params,
      deliverableId,
      projectId,
    }: {
      params: ProjectDeliverableParams;
      deliverableId: string;
      projectId: string;
    }) => {
      return apiInstance.put(
        API_ENDPOINTS.getProjectDeliverableById(deliverableId),
        { ...params, projectId },
      );
    },
  });
};

export const useAddExistingProjectDeliverableMutation = () => {
  return useMutation({
    mutationFn: ({
      params,
      projectId,
    }: {
      params: AddExistingProjectDeliverableParams;
      projectId: string;
    }) => {
      return apiInstance.post(
        API_ENDPOINTS.addExistingProjectDeliverable(projectId),
        params,
      );
    },
  });
};

export const useGetProjectDeliverableById = () => {
  const [projectId, setProjectId] = useState<string>("");
  const [deliverableId, setDeliverableId] = useState<string>("");

  const query = useQuery({
    queryKey: ["getProjectDeliverableById", projectId, deliverableId],
    queryFn: async () => {
      if (!projectId || !deliverableId) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getProjectDeliverableById(deliverableId, projectId),
      );
      return res.data;
    },
    enabled: !!deliverableId && !!projectId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (
    newDeliverableId: string,
    newProjectId: string,
  ) => {
    setDeliverableId(newDeliverableId);
    setProjectId(newProjectId);
  };

  return { ...query, refetchWithParams };
};

export const useGetProjectActivityList = () => {
  const [projectId, setProjectId] = useState<string>("");
  const [programTemplateId, setProgramTemplateId] = useState<string>();
  const query = useQuery({
    queryKey: ["getActivityList", projectId, programTemplateId],
    queryFn: async (): Promise<IdNameList[]> => {
      if (!projectId || !programTemplateId) {
        return [];
      }
      const queryParams = new URLSearchParams();
      queryParams.append("projectId", projectId);
      queryParams.append("programTemplateId", programTemplateId);
      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_ACTIVITY_LIST}?${queryParams.toString()}`,
      );
      return res.data;
    },
    enabled: !!projectId && !!programTemplateId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (projectId: string, programTemplateId: string) => {
    setProjectId(projectId);
    setProgramTemplateId(programTemplateId);
    query.refetch();
  };

  const clearListOfProjectActivity = () => {
    setProjectId("");
    setProgramTemplateId("");
    // it will return [] array
  };
  return { ...query, refetchWithParams, clearListOfProjectActivity };
};

export const useProjectActivationMutation = () => {
  return useMutation({
    mutationFn: async (
      projectId: string,
    ): Promise<GetProjectActivationResponse> => {
      const response = await apiInstance.put(
        API_ENDPOINTS.projectActivation(projectId ?? ""),
      );
      return response.data as GetProjectActivationResponse;
    },
    retry: false,
  });
};

export const useGetProjectStatusListId = () => {
  const [params, setParams] = useState<ProjectStatusParams>();

  const query = useQuery({
    queryKey: ["getProjectStatusList", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getProjectStatusListById(params.projectId ?? ""),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newStatusParams: ProjectStatusParams) => {
    setParams((prevParams) => ({
      ...newStatusParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useSaveProjectStatusMutation = () => {
  return useMutation({
    mutationFn: async (params: SaveProjectStatusParams) => {
      const response = await apiInstance.post(
        API_ENDPOINTS.saveProjectStatus(params.projectId),
        params,
      );
      return response.data;
    },
  });
};

export const useDeleteFeesAndExpense = () => {
  return useMutation({
    mutationFn: (feesAndExpenseId: string) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.getProjectExpenseById(feesAndExpenseId)}`,
      );
    },
  });
};

export const useGetParticipantActivitiesListByProjectId = () => {
  const [projectId, setProjectId] = useState<string>();

  const query = useQuery({
    queryKey: ["useGetParticipantActivitiesListByProjectId", projectId],
    queryFn: async () => {
      if (!projectId) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getActivitiesByProjectId(projectId ?? ""),
      );
      return res.data;
    },
    enabled: !!projectId,
    retry: false,
  });

  const refetchWithParams = (id: string) => {
    setProjectId(id);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetActivityTypesListByProjectId = () => {
  const [projectId, setProjectId] = useState<string>();

  const query = useQuery({
    queryKey: ["useActivityTypesListByProjectId", projectId],
    queryFn: async () => {
      if (!projectId) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getActivityTypeListByProjectId(projectId ?? ""),
      );
      return res.data as ActivityTypeList;
    },
    enabled: !!projectId, // Only run when params are set
    retry: false,
  });
  const refetchWithParams = (id: string) => {
    setProjectId(id);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetTopicListByProjectId = () => {
  const [projectId, setProjectId] = useState<string>();
  const [activityId, setActivityId] = useState<string>();

  const query = useQuery({
    queryKey: ["useGetTopicListByProjectId", projectId, activityId],
    queryFn: async () => {
      if (!projectId || !activityId) {
        return [];
      }

      const queryParams = new URLSearchParams();

      let url = `${API_ENDPOINTS.getTopicsByActivityIdAndProjectId(activityId ?? "", projectId ?? "")}?${queryParams.toString()}`;
      const res = await apiInstance.get(url);
      return res.data;
    },
    enabled: !!projectId && !!activityId, // Ensure query runs only when all params are set
    retry: false,
  });

  const refetchWithParams = (actId: string, projId: string) => {
    setProjectId(projId);
    setActivityId(actId);
  };

  const clearListOfTopicData = () => {
    setProjectId("");
    setActivityId("");
  };

  return { ...query, refetchWithParams, clearListOfTopicData };
};

export const useSaveActivityForProjectMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      projectId,
    }: {
      params: ProjectParticipantActivityParams;
      projectId: string;
    }) => {
      const response = apiInstance.post(
        API_ENDPOINTS.saveActivityForProject(projectId),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useUpdateActivityForProjectMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      participantActivityTimeLineId,
    }: {
      params: ProjectParticipantActivityParams;
      participantActivityTimeLineId: string;
    }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.getActivityTimelineById(participantActivityTimeLineId),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useDeleteProjectActivityTimeLineMutation = () => {
  return useMutation({
    mutationFn: (projectActivityTimeLineId: string) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.getActivityTimelineById(projectActivityTimeLineId)}`,
      );
    },
  });
};

export const useGetSearchProgramsForProjectMutation = () => {
  const [projectId, setProjectId] = useState<string>();
  const query = useQuery({
    queryKey: ["useGetSearchProgramsForProjectMutation", projectId],
    queryFn: async (): Promise<ChildProgramsListResponse[]> => {
      if (!projectId) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getSearchProgramsForProject(projectId),
      );
      return res.data;
    },
    enabled: !!projectId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (projectId: string) => {
    setProjectId(projectId);
  };
  return { ...query, refetchWithParams };
};
